import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface TestParameter {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  parameter_key: string;
  parameter_value: string;
  product_test_id: number;
}

interface TestParametersState {
  parameters: TestParameter[];
  loading: boolean;
  error: string | null;
}

const initialState: TestParametersState = {
  parameters: [],
  loading: false,
  error: null,
};

export const fetchTestParameters = createAsyncThunk(
  'testParameters/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/test-parameters`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch test parameters');
    }
  }
);

export const fetchTestParametersByTestId = createAsyncThunk(
  'testParameters/fetchByTestId',
  async (testId: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pms}/api/v1/test-parameters/by-test/${testId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch test parameters');
    }
  }
);

export const createTestParameter = createAsyncThunk(
  'testParameters/create',
  async (data: Partial<TestParameter>, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.pms}/api/v1/test-parameters`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to create parameter');
    }
  }
);

export const updateTestParameter = createAsyncThunk(
  'testParameters/update',
  async ({ id, data }: { id: number; data: Partial<TestParameter> }, thunkAPI) => {
    try {
      const response = await apiClient.put(`${config.services.pms}/api/v1/test-parameters/${id}`, data);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to update parameter');
    }
  }
);

export const deleteTestParameter = createAsyncThunk(
  'testParameters/delete',
  async (id: number, thunkAPI) => {
    try {
      await apiClient.delete(`${config.services.pms}/api/v1/test-parameters/${id}`);
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to delete parameter');
    }
  }
);

const testParametersSlice = createSlice({
  name: 'testParameters',
  initialState,
  reducers: {
    clearTestParametersError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestParameters.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTestParameters.fulfilled, (state, action) => { state.loading = false; state.parameters = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchTestParameters.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchTestParametersByTestId.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchTestParametersByTestId.fulfilled, (state, action) => { state.loading = false; state.parameters = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchTestParametersByTestId.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(createTestParameter.fulfilled, (state, action) => {
        const p = action.payload?.data || action.payload;
        if (p) state.parameters.push(p);
      })
      .addCase(updateTestParameter.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.parameters.findIndex(p => p.ID === updated.ID);
          if (idx !== -1) state.parameters[idx] = updated;
        }
      })
      .addCase(deleteTestParameter.fulfilled, (state, action) => {
        state.parameters = state.parameters.filter(p => p.ID !== action.payload);
      });
  },
});

export const { clearTestParametersError } = testParametersSlice.actions;
export default testParametersSlice.reducer;
